import { useEffect, useState } from 'react';
import { AlertCircle, CalendarClock, Loader2, Pencil, Plus, Trash2, Wrench, X } from 'lucide-react';
import {
  INTERVENTION_MAINTENANCE_STATUTS,
  INTERVENTION_MAINTENANCE_TYPES,
  type InterventionMaintenanceStatut,
  type InterventionMaintenanceType,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';
import { usePermissions } from '../../hooks/usePermissions';

/**
 * Panel des interventions de maintenance d'un équipement (US-EQ-08).
 *
 *  - Liste triée par date desc.
 *  - Encadré "Prochaine intervention" si présente.
 *  - Création / édition / suppression via mini-modal.
 *  - Calcul automatique de la prochaine échéance possible côté backend
 *    quand `type=preventive` + `interval_days`.
 */

interface PersonnelOption {
  id: string;
  first_name: string;
  last_name: string;
}

interface MaintenanceRow {
  id: string;
  date_intervention: string;
  type_intervention: InterventionMaintenanceType;
  duree_intervention: number | null;
  technicien_id: string | null;
  description: string | null;
  cout_intervention: number | null;
  prochaine_intervention: string | null;
  statut: InterventionMaintenanceStatut;
  observations: string | null;
  technicien: { id: string; first_name: string; last_name: string } | null;
}

export function MaintenancesPanel({ equipementId }: { equipementId: string }) {
  const { can } = usePermissions();
  const canEdit = can('equipements', 'update');
  const canDelete = can('equipements', 'delete');

  const [items, setItems] = useState<MaintenanceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [editing, setEditing] = useState<MaintenanceRow | null>(null);
  const [creating, setCreating] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const res = await api.get<{ data: MaintenanceRow[] }>(
        `/equipements/${equipementId}/maintenances`,
      );
      setItems(res.data.data);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipementId]);

  async function handleDelete(id: string) {
    if (!confirm('Supprimer cette intervention ?')) return;
    try {
      await api.delete(`/equipements/maintenances/${id}`);
      refresh();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="w-5 h-5 animate-spin text-primary-500" />
      </div>
    );
  }

  // Prochaine intervention = celle planifiée la plus proche dans le futur.
  const now = new Date();
  const nextOne = items
    .filter(
      (m) =>
        m.statut !== 'terminee' && m.statut !== 'annulee' && new Date(m.date_intervention) >= now,
    )
    .sort(
      (a, b) => new Date(a.date_intervention).getTime() - new Date(b.date_intervention).getTime(),
    )[0];

  return (
    <div className="space-y-3">
      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      {nextOne && (
        <div className="rounded-lg border border-orange-200 bg-orange-50 p-3 flex items-start space-x-2">
          <CalendarClock className="w-4 h-4 text-orange-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-orange-800">Prochaine intervention</p>
            <p className="text-orange-700">
              {new Date(nextOne.date_intervention).toLocaleDateString()} —{' '}
              {nextOne.type_intervention} ({nextOne.statut.replace(/_/g, ' ')})
              {nextOne.technicien
                ? ` · ${nextOne.technicien.first_name} ${nextOne.technicien.last_name}`
                : ''}
            </p>
          </div>
        </div>
      )}

      {canEdit && (
        <div className="flex justify-end">
          <button
            onClick={() => setCreating(true)}
            className="inline-flex items-center px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg text-sm font-medium"
          >
            <Plus className="w-4 h-4 mr-1" />
            Nouvelle intervention
          </button>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-gray-500 italic">Aucune intervention enregistrée.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((m) => (
            <li
              key={m.id}
              className="rounded-lg border border-gray-200 p-3 bg-white flex items-start justify-between gap-2"
            >
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 flex items-center flex-wrap gap-2">
                  <Wrench className="w-3.5 h-3.5 text-gray-400" />
                  <span>{new Date(m.date_intervention).toLocaleDateString()}</span>
                  <TypeBadge type={m.type_intervention} />
                  <StatutBadge statut={m.statut} />
                  {m.duree_intervention != null && (
                    <span className="text-xs text-gray-500">{m.duree_intervention} min</span>
                  )}
                  {m.cout_intervention != null && (
                    <span className="text-xs text-gray-500">{m.cout_intervention} €</span>
                  )}
                </p>
                {m.technicien && (
                  <p className="text-xs text-gray-500 mt-0.5">
                    Technicien : {m.technicien.first_name} {m.technicien.last_name}
                  </p>
                )}
                {m.description && <p className="text-xs text-gray-600 mt-1">{m.description}</p>}
                {m.prochaine_intervention && (
                  <p className="text-xs text-gray-500 mt-1">
                    Prochaine : {new Date(m.prochaine_intervention).toLocaleDateString()}
                  </p>
                )}
              </div>
              {(canEdit || canDelete) && (
                <div className="flex items-center space-x-1 flex-shrink-0">
                  {canEdit && (
                    <button
                      onClick={() => setEditing(m)}
                      className="p-1.5 rounded text-gray-600 hover:bg-gray-100"
                      title="Modifier"
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {canDelete && (
                    <button
                      onClick={() => handleDelete(m.id)}
                      className="p-1.5 rounded text-red-600 hover:bg-red-50"
                      title="Supprimer"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {creating && (
        <MaintenanceFormModal
          equipementId={equipementId}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
      {editing && (
        <MaintenanceFormModal
          equipementId={equipementId}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function MaintenanceFormModal({
  equipementId,
  existing,
  onClose,
  onSaved,
}: {
  equipementId: string;
  existing?: MaintenanceRow;
  onClose(): void;
  onSaved(): void;
}) {
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [type, setType] = useState<InterventionMaintenanceType>(
    existing?.type_intervention ?? 'preventive',
  );
  const [date, setDate] = useState<string>(
    existing
      ? new Date(existing.date_intervention).toISOString().slice(0, 16)
      : new Date().toISOString().slice(0, 16),
  );
  const [statut, setStatut] = useState<InterventionMaintenanceStatut>(
    existing?.statut ?? 'planifiee',
  );
  const [duree, setDuree] = useState<string>(
    existing?.duree_intervention != null ? String(existing.duree_intervention) : '',
  );
  const [technicienId, setTechnicienId] = useState<string>(existing?.technicien_id ?? '');
  const [description, setDescription] = useState<string>(existing?.description ?? '');
  const [cout, setCout] = useState<string>(
    existing?.cout_intervention != null ? String(existing.cout_intervention) : '',
  );
  const [prochaine, setProchaine] = useState<string>(
    existing?.prochaine_intervention
      ? new Date(existing.prochaine_intervention).toISOString().slice(0, 10)
      : '',
  );
  const [intervalDays, setIntervalDays] = useState<string>('');
  const [observations, setObservations] = useState<string>(existing?.observations ?? '');

  const [techniciens, setTechniciens] = useState<PersonnelOption[]>([]);

  useEffect(() => {
    api
      .get<{ data: PersonnelOption[] }>('/personnels?status=active&page_size=200')
      .then((res) => setTechniciens(res.data.data))
      .catch(() => setTechniciens([]));
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setErrorMessage(null);
    try {
      const payload = {
        date_intervention: new Date(date).toISOString(),
        type_intervention: type,
        statut,
        duree_intervention: duree ? Number(duree) : null,
        technicien_id: technicienId || null,
        description: description || null,
        cout_intervention: cout ? Number(cout) : null,
        prochaine_intervention: prochaine || null,
        interval_days: intervalDays ? Number(intervalDays) : null,
        observations: observations || null,
      };
      if (existing) {
        await api.patch(`/equipements/maintenances/${existing.id}`, payload);
      } else {
        await api.post(`/equipements/${equipementId}/maintenances`, payload);
      }
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl max-w-lg w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">
              {existing ? "Modifier l'intervention" : 'Nouvelle intervention'}
            </h3>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 space-y-3">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <Field label="Type *">
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value as InterventionMaintenanceType)}
                  className={inputClass}
                >
                  {INTERVENTION_MAINTENANCE_TYPES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Statut *">
                <select
                  value={statut}
                  onChange={(e) => setStatut(e.target.value as InterventionMaintenanceStatut)}
                  className={inputClass}
                >
                  {INTERVENTION_MAINTENANCE_STATUTS.map((s) => (
                    <option key={s} value={s}>
                      {s.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Date *" className="col-span-2">
                <input
                  type="datetime-local"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className={inputClass}
                  required
                />
              </Field>
              <Field label="Durée (min)">
                <input
                  type="number"
                  min="0"
                  value={duree}
                  onChange={(e) => setDuree(e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Coût (€)">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={cout}
                  onChange={(e) => setCout(e.target.value)}
                  className={inputClass}
                />
              </Field>
              <Field label="Technicien" className="col-span-2">
                <select
                  value={technicienId}
                  onChange={(e) => setTechnicienId(e.target.value)}
                  className={inputClass}
                >
                  <option value="">— Aucun —</option>
                  {techniciens.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.last_name} {t.first_name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Description" className="col-span-2">
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={2}
                  className={inputClass}
                />
              </Field>
              {type === 'preventive' && (
                <>
                  <Field label="Prochaine échéance">
                    <input
                      type="date"
                      value={prochaine}
                      onChange={(e) => setProchaine(e.target.value)}
                      className={inputClass}
                    />
                  </Field>
                  <Field label="Intervalle (jours)">
                    <input
                      type="number"
                      min="1"
                      value={intervalDays}
                      onChange={(e) => setIntervalDays(e.target.value)}
                      className={inputClass}
                    />
                  </Field>
                </>
              )}
              <Field label="Observations" className="col-span-2">
                <textarea
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                  rows={2}
                  className={inputClass}
                />
              </Field>
            </div>
          </form>

          <div className="flex items-center justify-end gap-2 p-4 border-t border-gray-200 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving}
              className="inline-flex items-center px-4 py-2 rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
            >
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {existing ? 'Enregistrer' : 'Créer'}
            </button>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function TypeBadge({ type }: { type: InterventionMaintenanceType }) {
  const map: Record<InterventionMaintenanceType, string> = {
    preventive: 'bg-blue-100 text-blue-800 border-blue-200',
    curative: 'bg-orange-100 text-orange-800 border-orange-200',
    controle: 'bg-purple-100 text-purple-800 border-purple-200',
  };
  return (
    <span
      className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-medium border ${map[type]}`}
    >
      {type}
    </span>
  );
}

function StatutBadge({ statut }: { statut: InterventionMaintenanceStatut }) {
  const map: Record<InterventionMaintenanceStatut, string> = {
    planifiee: 'bg-gray-100 text-gray-700 border-gray-200',
    en_cours: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    terminee: 'bg-green-100 text-green-800 border-green-200',
    annulee: 'bg-red-100 text-red-800 border-red-200',
  };
  return (
    <span
      className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-medium border ${map[statut]}`}
    >
      {statut.replace(/_/g, ' ')}
    </span>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function Field({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-xs font-medium text-gray-700 mb-1">{label}</label>
      {children}
    </div>
  );
}
